<script lang="ts">
    import Link from './Link.svelte';
    import { onMount } from 'svelte';
    import { auth } from '../lib/stores/auth.svelte';
    import { envs } from '../lib/stores/environment.svelte';
    import { i18n, t } from '../lib/i18n.svelte';

    let { children } = $props();

    onMount(() => {
        envs.load();
        i18n.hydrate();
    });

    const navItems = [
        { path: '/', label: 'Dashboard', icon: '📊' },
        { path: '/secrets', label: 'Secrets', icon: '🔑' },
        { path: '/missing', label: 'Missing requests', icon: '🕳️' },
        { path: '/consumers', label: 'Apps', icon: '📱' },
        { path: '/environments', label: 'Environments', icon: '🌍' },
        { path: '/webhooks', label: 'Webhooks', icon: '🔔' },
        { path: '/audit', label: 'Audit Log', icon: '📜' },
        { path: '/backups', label: 'Backups', icon: '💾' },
    ];

    // SP-295D4: registry-driven cycle through activated locales instead of the
    // closed en↔nb toggle (hydrated from the backend's Mother proxy route).
    function toggleLocale() {
        i18n.cycleLocale();
    }
</script>

<div class="shell">
    <aside class="sidebar">
        <div class="brand">
            <span class="logo"><img src="/brand/SQUARE1.png" alt="Sprezzatura logo" /></span>
            <span class="title brand-stack"><span class="company">Sprezzatura</span><span class="app">KeyVault</span></span>
        </div>

        <nav>
            {#each navItems as item}
                <Link to={item.path} class="nav-link">
                    <span class="nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                </Link>
            {/each}
        </nav>

        <div class="sidebar-footer">
            <div class="user">
                <div class="muted" style="font-size:11px;">Signed in as</div>
                <div>{auth.username}</div>
            </div>
            <button
                class="ghost"
                onclick={toggleLocale}
                title={t('layout.languageToggle', { en: 'Switch language', nb: 'Bytt språk' })}
            >
                {#if i18n.locale === 'en'}🇬🇧 EN{:else if i18n.locale === 'nb'}🇳🇴 NB{:else}🌐 {i18n.locale.toUpperCase()}{/if}
            </button>
            <button class="ghost" onclick={() => auth.logout()}>Sign out</button>
        </div>
    </aside>

    <main class="content">
        {#if envs.current}
            <div class="env-banner" style="background: {envs.current.themeColor};">
                <div class="env-banner-inner">
                    <span>Environment:</span>
                    <strong>{envs.current.name}</strong>
                    <select
                            value={envs.currentId}
                            onchange={(e) => envs.setCurrent((e.target as HTMLSelectElement).value)}
                    >
                        {#each envs.list as e}
                            <option value={e.id}>{e.name}</option>
                        {/each}
                    </select>
                </div>
            </div>
        {/if}

        <div class="page">{@render children()}</div>
    </main>
</div>

<style>
    .shell { display: flex; height: 100vh; }
    .sidebar {
        width: 220px;
        background: var(--bg-1);
        border-right: 1px solid var(--border);
        display: flex;
        flex-direction: column;
        padding: 16px 0;
    }
    .brand {
        display: flex; align-items: center; gap: 10px;
        padding: 0 20px 20px; border-bottom: 1px solid var(--border);
        margin-bottom: 12px;
    }
    .logo { font-size: 24px; display: flex; }
    .logo img { width: 26px; height: 26px; object-fit: contain; }
    .title { font-weight: 700; font-size: 16px; }
    .brand-stack { display: flex; flex-direction: column; line-height: 1.2; font-weight: 700; }
    .brand-stack .company { font-size: 14px; }
    .brand-stack .app { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.7; }
    nav { flex: 1; display: flex; flex-direction: column; padding: 0 8px; gap: 2px; }
    :global(.nav-link) {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 12px; border-radius: var(--radius);
        color: var(--text-2); text-decoration: none;
        font-size: 14px;
    }
    :global(.nav-link:hover) { background: var(--bg-2); color: var(--text-1); }
    .nav-icon { font-size: 16px; width: 20px; text-align: center; }
    .sidebar-footer {
        padding: 12px 16px; border-top: 1px solid var(--border);
        display: flex; flex-direction: column; gap: 8px;
    }
    .user { font-size: 13px; }
    .content { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
    .env-banner {
        padding: 6px 24px;
        color: white;
        box-shadow: inset 0 -2px 0 rgba(0,0,0,0.2);
    }
    .env-banner-inner {
        display: flex; align-items: center; gap: 12px;
        font-size: 13px;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
    }
    .env-banner select {
        background: rgba(0,0,0,0.25);
        color: white; border: 1px solid rgba(255,255,255,0.3);
        padding: 2px 8px;
        width: auto;
    }
    .page { flex: 1; overflow: auto; padding: 24px; }
</style>